import type {
  Account,
  Goal,
  PlanAssumptions,
  RetirementProfile,
  Transaction,
} from '@/api/generated/model';
import { computeNetWorth } from '@/features/accounts/buildNetWorthHistory';
import { format } from 'date-fns';
import { goalCategory, isGoalActive } from './goalProjection';
import { resolveRecurrentPayments, sumRecurrentForYear } from './recurrentPayments';

const DEFAULT_PAST_MONTHS = 24;

export interface NetWorthTimelinePoint {
  timestamp: number;
  label: string;
  value: number;
  phase: 'past' | 'today' | 'future';
  pastValue: number | null;
  futureValue: number | null;
}

export interface GoalTimelineMarker {
  id: string;
  name: string;
  timestamp: number;
  label: string;
  value: number;
  targetAmount: number;
}

export interface NetWorthTimelineInput {
  accounts: Account[];
  transactions: Transaction[];
  assumptions: PlanAssumptions;
  goals: Goal[];
  retirement: RetirementProfile;
  rangeYears: number;
  birthYear?: number;
  pastMonths?: number;
}

export interface NetWorthTimelineResult {
  points: NetWorthTimelinePoint[];
  goalMarkers: GoalTimelineMarker[];
  todayTimestamp: number;
  currentNetWorth: number;
  projectedEndValue: number;
  projectedEndLabel: string;
}

function monthEnd(year: number, month: number): Date {
  return new Date(year, month + 1, 0, 12, 0, 0, 0);
}

function formatAxisLabel(date: Date): string {
  return format(date, 'MMM yyyy');
}

function formatTooltipLabel(date: Date): string {
  return format(date, 'MMM d, yyyy');
}

function goalImpactForMonth(goals: Goal[], date: Date): number {
  let impact = 0;
  for (const goal of goals) {
    if (!isGoalActive(goal) || !goal.dueDate) continue;
    const due = new Date(goal.dueDate);
    if (due.getFullYear() !== date.getFullYear() || due.getMonth() !== date.getMonth()) {
      continue;
    }
    const category = goalCategory(goal);
    if (category === 'career' || category === 'lifestyle') {
      impact += goal.targetAmount - goal.currentAmount;
    }
  }
  return impact;
}

function averageMonthlyGrowthRate(accounts: Account[], assumptions: PlanAssumptions): number {
  const assetAccounts = accounts.filter((a) => a.type !== 'liability');
  if (assetAccounts.length === 0) return 0;
  let weighted = 0;
  let totalWeight = 0;
  for (const account of assetAccounts) {
    const custom = assumptions.accountGrowthRates.find((r) => r.accountId === account.id);
    let rate = custom?.annualGrowthRate ?? 0.02;
    if (!custom) {
      if (['bank', 'checking', 'savings'].includes(account.type)) rate = 0.01;
      if (['real_estate', 'property'].includes(account.type)) rate = 0.03;
    }
    weighted += rate * Math.abs(account.balance);
    totalWeight += Math.abs(account.balance);
  }
  const annual = totalWeight > 0 ? weighted / totalWeight : 0.02;
  return Math.pow(1 + annual, 1 / 12) - 1;
}

function buildPastPoints(
  transactions: Transaction[],
  currentNetWorth: number,
  pastMonths: number,
  now: Date
): NetWorthTimelinePoint[] {
  const sortedTx = transactions
    .filter((tx) => tx.asOf)
    .sort((a, b) => new Date(b.asOf!).getTime() - new Date(a.asOf!).getTime());

  const points: NetWorthTimelinePoint[] = [];

  for (let offset = pastMonths; offset >= 0; offset -= 1) {
    const anchor = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    const date = offset === 0 ? now : monthEnd(anchor.getFullYear(), anchor.getMonth());
    if (date > now) continue;

    let value = currentNetWorth;
    for (const tx of sortedTx) {
      if (new Date(tx.asOf!) > date) {
        value -= tx.amount;
      }
    }

    const phase = offset === 0 ? 'today' : 'past';
    points.push({
      timestamp: date.getTime(),
      label: formatAxisLabel(date),
      value,
      phase,
      pastValue: value,
      futureValue: null,
    });
  }

  if (points.length === 0) {
    points.push({
      timestamp: now.getTime(),
      label: formatAxisLabel(now),
      value: currentNetWorth,
      phase: 'today',
      pastValue: currentNetWorth,
      futureValue: currentNetWorth,
    });
  } else {
    const last = points[points.length - 1];
    last.phase = 'today';
    last.futureValue = last.value;
  }

  return points;
}

function buildFuturePoints(
  startBalance: number,
  startDate: Date,
  input: NetWorthTimelineInput
): NetWorthTimelinePoint[] {
  const { accounts, assumptions, goals, retirement, rangeYears, birthYear } = input;
  const recurrentPayments = resolveRecurrentPayments(assumptions);
  const monthlyGrowth = averageMonthlyGrowthRate(accounts, assumptions);
  const birth = birthYear ?? new Date().getFullYear() - 40;
  const monthsForward = rangeYears * 12;
  const points: NetWorthTimelinePoint[] = [];

  let balance = startBalance;

  for (let monthOffset = 1; monthOffset <= monthsForward; monthOffset += 1) {
    const calendar = new Date(startDate.getFullYear(), startDate.getMonth() + monthOffset, 1);
    const yearIndex = Math.floor((monthOffset - 1) / 12);
    const age = calendar.getFullYear() - birth;
    const isRetired = age >= retirement.retirementAge;

    const { income: annualIncome, expenses: annualExpenses } = sumRecurrentForYear(
      recurrentPayments,
      yearIndex
    );

    let income = annualIncome;
    let expenses = annualExpenses;
    if (isRetired) {
      income = age >= retirement.statePensionAge ? retirement.statePensionAnnualAmount : 0;
      expenses = retirement.annualSpendingTarget;
    }

    const monthlyIncome = income / 12;
    const monthlyTax = monthlyIncome * assumptions.effectiveTaxRate;
    const monthlyExpense = expenses / 12;

    const incomeDate = new Date(calendar.getFullYear(), calendar.getMonth(), 1, 12, 0, 0, 0);
    balance += monthlyIncome - monthlyTax;
    points.push({
      timestamp: incomeDate.getTime(),
      label: formatAxisLabel(incomeDate),
      value: balance,
      phase: 'future',
      pastValue: null,
      futureValue: balance,
    });

    balance -= monthlyExpense;
    balance += goalImpactForMonth(goals, incomeDate);
    balance *= 1 + monthlyGrowth;

    const expenseDate = monthEnd(calendar.getFullYear(), calendar.getMonth());
    points.push({
      timestamp: expenseDate.getTime(),
      label: formatAxisLabel(expenseDate),
      value: balance,
      phase: 'future',
      pastValue: null,
      futureValue: balance,
    });
  }

  return points;
}

function buildGoalMarkers(
  goals: Goal[],
  points: NetWorthTimelinePoint[],
  todayTimestamp: number
): GoalTimelineMarker[] {
  return goals
    .filter((goal) => isGoalActive(goal) && goal.dueDate)
    .map((goal) => {
      const due = new Date(goal.dueDate!);
      if (due.getTime() < todayTimestamp) return null;

      const futurePoints = points.filter((p) => p.phase === 'future');
      if (futurePoints.length === 0) return null;

      const closest = futurePoints.reduce((best, point) => {
        const bestDelta = Math.abs(best.timestamp - due.getTime());
        const pointDelta = Math.abs(point.timestamp - due.getTime());
        return pointDelta < bestDelta ? point : best;
      });

      return {
        id: goal.id,
        name: goal.name,
        timestamp: closest.timestamp,
        label: formatAxisLabel(due),
        value: closest.value,
        targetAmount: goal.targetAmount,
      };
    })
    .filter((marker): marker is GoalTimelineMarker => marker != null);
}

export function buildNetWorthTimeline(input: NetWorthTimelineInput): NetWorthTimelineResult {
  const now = new Date();
  const currentNetWorth = computeNetWorth(input.accounts, null);
  const pastMonths = input.pastMonths ?? DEFAULT_PAST_MONTHS;

  const pastPoints = buildPastPoints(input.transactions, currentNetWorth, pastMonths, now);
  const todayPoint = pastPoints[pastPoints.length - 1];
  const todayTimestamp = todayPoint.timestamp;

  const futurePoints = buildFuturePoints(todayPoint.value, now, input);
  const points = [...pastPoints.slice(0, -1), todayPoint, ...futurePoints];

  const goalMarkers = buildGoalMarkers(input.goals, points, todayTimestamp);
  const lastFuture = futurePoints[futurePoints.length - 1];

  return {
    points,
    goalMarkers,
    todayTimestamp,
    currentNetWorth,
    projectedEndValue: lastFuture?.value ?? currentNetWorth,
    projectedEndLabel: lastFuture
      ? formatAxisLabel(new Date(lastFuture.timestamp))
      : formatAxisLabel(now),
  };
}

export { formatTooltipLabel };
